/**
 * ReaderHome — Startseite für Menschen, die nur lesen oder anmerken.
 *
 * Alex 2026-10-02, nach der ersten Anmeldung mit einem Team-Konto der
 * Drone Challenge: „wirkt ein wenig empty und verloren". Die Startseite
 * war die Redaktionsliste „All Posts": schmale Zeilen links oben, IDs,
 * Status-Marken, Autor-Agent, Tags, dazu „New" und Löschen — alles Dinge,
 * die ein Team-Mitglied weder braucht noch darf.
 *
 * Für diese Rollen ist das Portal eine kleine Dokumentensammlung. Darum:
 * eine zentrierte Seite mit dem Namen des Mandanten, den Dokumenten als
 * Karten in Lesereihenfolge und einem Satz, wie man anmerkt.
 *
 * Lesereihenfolge: Beginnt der Untertitel mit „Teil N", wird danach
 * sortiert und „Teil N" als Marke gezeigt; der Rest folgt in der
 * Reihenfolge der Anlage.
 */

import type { Post } from '../types'

const PART_RE = /^\s*Teil\s+(\d+)\b[^·]*(?:·\s*)?/i

interface Entry {
  post: Post
  part: number | null
  description: string
}

function toEntry(post: Post): Entry {
  const subtitle = post.subtitle || ''
  const m = subtitle.match(PART_RE)
  if (m) return { post, part: Number(m[1]), description: subtitle.slice(m[0].length).trim() }
  return { post, part: null, description: subtitle.trim() }
}

function inReadingOrder(posts: Post[]): Entry[] {
  const entries = posts.map(toEntry)
  return entries.sort((a, b) => {
    if (a.part !== null && b.part !== null) return a.part - b.part
    if (a.part !== null) return -1
    if (b.part !== null) return 1
    return a.post.created_at.localeCompare(b.post.created_at)
  })
}

/** „drone-challenge" → „Drone Challenge". */
function tenantDisplayName(tenant: string): string {
  return tenant
    .split(/[-_]/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')
}

export function ReaderHome({
  posts,
  loading,
  tenants,
  userName,
  canComment,
  onOpen,
}: {
  posts: Post[]
  loading: boolean
  tenants: string[]
  userName: string
  canComment: boolean
  onOpen: (post: Post) => void
}) {
  const entries = inReadingOrder(posts)
  const heading = tenants.length === 1 ? tenantDisplayName(tenants[0]) : 'Dokumente'
  const count = entries.length === 1 ? '1 Dokument' : `${entries.length} Dokumente`

  return (
    <div className="reader-home">
      <header className="reader-home__head">
        <div className="reader-home__kicker">Dokumentation</div>
        <h1 className="reader-home__title">{heading}</h1>
        <p className="reader-home__sub">
          {loading ? 'Lädt …' : count}
          {userName && <> · angemeldet als <strong>{userName}</strong></>}
        </p>
      </header>

      {!loading && entries.length === 0 && (
        <p className="reader-home__empty">Für dieses Konto sind noch keine Dokumente freigegeben.</p>
      )}

      <div className="reader-home__grid">
        {entries.map(({ post, part, description }, i) => (
          <button key={post.id} type="button" className="reader-card" onClick={() => onOpen(post)}>
            <span className="reader-card__num">{String(part ?? i + 1).padStart(2, '0')}</span>
            <span className="reader-card__body">
              <span className="reader-card__label">{part !== null ? `Teil ${part}` : 'Referenz'}</span>
              <span className="reader-card__title">{post.title}</span>
              {description && <span className="reader-card__desc">{description}</span>}
              <span className="reader-card__meta">
                Stand {new Date(post.updated_at || post.created_at).toLocaleDateString('de-AT')}
                <span className="reader-card__open">Öffnen →</span>
              </span>
            </span>
          </button>
        ))}
      </div>

      {canComment && entries.length > 0 && (
        <aside className="reader-home__hint">
          <strong>Fragen und Anmerkungen:</strong> Im Dokument eine Textstelle markieren und
          „Anmerken" wählen. Die Anmerkung erscheint an genau dieser Stelle am Seitenrand, dort
          könnt ihr auch auf Antworten reagieren. Den Text der Dokumente selbst könnt ihr nicht
          verändern.
        </aside>
      )}
    </div>
  )
}
